import { settingApi } from '../../../slice/settingApi';

// Types for Role Management
export interface IRole {
  id: string;
  name: string;
  description: string;
  roleType: string;
  isCustom: boolean;
  dataScope: string;
  status: string;
  permissions: any[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface IRoleAssignedUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface IRoleDetails extends IRole {
  assignedUsers?: IRoleAssignedUser[];
}

export interface IDeleteRoleResult {
  success: boolean;
  message: string;
  deleted: boolean;
  reassignedUserCount: number;
  reassignedTo?: { id: string; name: string };
}

export type DeleteRoleParams = {
  roleId: string;
  reassignToRoleId?: string;
};

export interface IModuleAccess {
  id: string;
  roleId: string;
  moduleId: string;
  isEnabled: boolean;
  module: {
    id: string;
    name: string;
  };
}

// Types for Permission Overrides
export interface IUserPermission {
  id: string;
  permissionCode: string;
  permissionLabel?: string;
  grantedAt: string;
  grantedBy: string;
  expiresAt: string | null;
  isExpired: boolean;
}

export const rbacApi = settingApi.injectEndpoints({
  endpoints: (builder) => ({
    // 1) Role Management APIs
    createRole: builder.mutation({
      query: (data) => ({
        url: 'api/admin/roles',
        method: 'POST',
        body: data,
      }),
    }),

    listRoles: builder.mutation({
      query: (data) => ({
        url: 'api/admin/roles/list',
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: { data: IRole[] }) => response.data,
    }),

    getRoleDetails: builder.query<IRoleDetails, string>({
      query: (roleId) => ({
        url: `api/admin/roles/${roleId}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: IRoleDetails }) => response.data,
    }),

    updateRole: builder.mutation({
      query: ({ roleId, ...data }) => ({
        url: `api/admin/roles/${roleId}`,
        method: 'PUT',
        body: data,
      }),
    }),

    deleteRole: builder.mutation<IDeleteRoleResult, DeleteRoleParams>({
      query: ({ roleId, reassignToRoleId }) => ({
        url: `api/admin/roles/${roleId}`,
        method: 'DELETE',
        ...(reassignToRoleId ? { body: { reassignToRoleId } } : {}),
      }),
      transformResponse: (response: { data?: IDeleteRoleResult } & IDeleteRoleResult) =>
        response.data ?? response,
    }),

    assignPermissionsToRole: builder.mutation({
      query: ({ roleId, permissionIds }) => ({
        url: `api/admin/roles/${roleId}/permissions/assign`,
        method: 'POST',
        body: { permissionIds },
      }),
    }),

    removePermissionsFromRole: builder.mutation({
      query: ({ roleId, permissionIds }) => ({
        url: `api/admin/roles/${roleId}/permissions/remove`,
        method: 'POST',
        body: { permissionIds },
      }),
    }),

    setModuleAccess: builder.mutation({
      query: (data) => ({
        url: 'api/admin/roles/module-access/set',
        method: 'POST',
        body: data,
      }),
    }),

    getModuleAccessByRole: builder.query({
      query: (roleId) => ({
        url: `api/admin/roles/${roleId}/module-access`,
        method: 'GET',
      }),
      transformResponse: (response: { data: IModuleAccess[] }) => response.data,
    }),

    assignRoleToUser: builder.mutation({
      query: (data) => ({
        url: 'api/admin/roles/user-assignment/assign',
        method: 'POST',
        body: data,
      }),
    }),

    removeRoleFromUser: builder.mutation({
      query: (data) => ({
        url: 'api/admin/roles/user-assignment/remove',
        method: 'POST',
        body: data,
      }),
    }),

    getUserRoles: builder.query({
      query: (userId) => ({
        url: `api/admin/roles/user/${userId}/roles`,
        method: 'GET',
      }),
      transformResponse: (response: { data: IRole[] }) => response.data,
    }),

    getUserEffectivePermissions: builder.query({
      query: (userId) => ({
        url: `api/admin/roles/user/${userId}/effective-permissions`,
        method: 'GET',
      }),
      transformResponse: (response: { data: string[] }) => response.data,
    }),

    getAvailablePermissions: builder.query({
      query: () => ({
        url: 'api/admin/roles/reference/permissions',
        method: 'GET',
      }),
      transformResponse: (response: { data: any[] }) => response.data,
    }),

    getAvailableModules: builder.query({
      query: () => ({
        url: 'api/admin/roles/reference/modules',
        method: 'GET',
      }),
      transformResponse: (response: { data: any[] }) => response.data,
    }),

    getRoleAuditChangeLog: builder.mutation({
      query: (data) => ({
        url: 'api/admin/roles/audit/change-log',
        method: 'POST',
        body: data,
      }),
    }),

    // 2) Permission Override APIs
    grantDirectPermission: builder.mutation({
      query: (data) => ({
        url: 'api/admin/permissions/grant',
        method: 'POST',
        body: data,
      }),
    }),

    revokeDirectPermission: builder.mutation({
      query: (data) => ({
        url: 'api/admin/permissions/revoke',
        method: 'POST',
        body: data,
      }),
    }),

    getUserDirectPermissions: builder.query({
      query: (userId) => ({
        url: `api/admin/permissions/user/${userId}/direct`,
        method: 'GET',
      }),
      transformResponse: (response: { data: IUserPermission[] }) => response.data,
    }),

    getUserPermissionDetails: builder.query({
      query: (userId) => ({
        url: `api/admin/permissions/user/${userId}/details`,
        method: 'GET',
      }),
      transformResponse: (response: { data: any }) => response.data,
    }),

    getUsersWithOverrides: builder.mutation({
      query: (data) => ({
        url: 'api/admin/permissions/users-with-overrides',
        method: 'POST',
        body: data,
      }),
    }),

    getUserOverrideHistory: builder.mutation({
      query: ({ userId, ...data }) => ({
        url: `api/admin/permissions/user/${userId}/history`,
        method: 'POST',
        body: { userId, ...data }, // Body contains userId as per notes
      }),
    }),

    bulkGrantPermissions: builder.mutation({
      query: (data) => ({
        url: 'api/admin/permissions/bulk-grant',
        method: 'POST',
        body: data,
      }),
    }),

    cleanupExpiredPermissions: builder.mutation({
      query: () => ({
        url: 'api/admin/permissions/cleanup-expired',
        method: 'POST',
        body: {},
      }),
    }),

    listExpiredPermissions: builder.mutation({
      query: (data) => ({
        url: 'api/admin/permissions/list-expired',
        method: 'POST',
        body: data,
      }),
    }),

    // 3) Permission Audit APIs
    getPermissionAuditLog: builder.mutation({
      query: (data) => ({
        url: 'api/admin/audit/permissions/log',
        method: 'POST',
        body: data,
      }),
    }),

    getPermissionAuditSummary: builder.mutation({
      query: (data) => ({
        url: 'api/admin/audit/permissions/summary',
        method: 'POST',
        body: data,
      }),
    }),

    exportPermissionAuditLogs: builder.mutation({
      query: (data) => ({
        url: 'api/admin/audit/permissions/export',
        method: 'POST',
        body: data,
        responseHandler: (response) => response.blob(), // For CSV download
      }),
    }),
  }),
});

export const {
  useCreateRoleMutation,
  useListRolesMutation,
  useGetRoleDetailsQuery,
  useLazyGetRoleDetailsQuery,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useAssignPermissionsToRoleMutation,
  useRemovePermissionsFromRoleMutation,
  useSetModuleAccessMutation,
  useGetModuleAccessByRoleQuery,
  useAssignRoleToUserMutation,
  useRemoveRoleFromUserMutation,
  useGetUserRolesQuery,
  useGetUserEffectivePermissionsQuery,
  useGetAvailablePermissionsQuery,
  useGetAvailableModulesQuery,
  useGetRoleAuditChangeLogMutation,
  useGrantDirectPermissionMutation,
  useRevokeDirectPermissionMutation,
  useGetUserDirectPermissionsQuery,
  useGetUserPermissionDetailsQuery,
  useGetUsersWithOverridesMutation,
  useGetUserOverrideHistoryMutation,
  useBulkGrantPermissionsMutation,
  useCleanupExpiredPermissionsMutation,
  useListExpiredPermissionsMutation,
  useGetPermissionAuditLogMutation,
  useGetPermissionAuditSummaryMutation,
  useExportPermissionAuditLogsMutation,
} = rbacApi;
